/**
 * Toolchain Checker — runs check commands defined in toolchain-contracts
 * and returns structured pass/fail results.
 *
 * Each toolchain may declare a `checks` map (checkType → shell command).
 * This module iterates all checks, executes them via subprocess, and
 * returns a ToolchainCheckResult[].
 *
 * For "drift" check types, a manifest-based fast path is available:
 * if a .toolchain-manifest.json exists and the toolchain_id matches,
 * the command execution can be skipped when the source hash is current.
 */

import { execFile } from "node:child_process";
import { readFile } from "node:fs/promises";
import { existsSync } from "node:fs";
import { resolve } from "node:path";
import { promisify } from "node:util";
import { z } from "zod";
import { parseToolchainFile } from "@aaac/toolchain";
import {
  emitToolchainOpen,
  emitToolchainClose,
  buildObservabilityEnv,
  type ObserverEmitFn,
  type ObservabilityIds,
} from "./observability-bridge.js";

const execFileAsync = promisify(execFile);

const SUBPROCESS_TIMEOUT_MS = 30_000;
const MANIFEST_FILENAME = ".toolchain-manifest.json";

// ---------------------------------------------------------------------------
// Manifest schema (Phase 7 full validation; schema + read logic placed here)
// ---------------------------------------------------------------------------

export const ToolchainManifestSchema = z.object({
  schema: z.string(),
  toolchain_id: z.string(),
  generated_at: z.string(),
  source_hash: z.string(),
  output_hash: z.string(),
  source_globs: z.array(z.string()).optional(),
  output_globs: z.array(z.string()).optional(),
  files: z
    .array(
      z.object({
        path: z.string(),
        hash: z.string(),
      }),
    )
    .optional(),
});

export type ToolchainManifest = z.infer<typeof ToolchainManifestSchema>;

// ---------------------------------------------------------------------------
// Check result types
// ---------------------------------------------------------------------------

export interface ToolchainCheckResult {
  toolchainId: string;
  checkType: string;
  status: "pass" | "fail" | "skip" | "error";
  action: "block" | "warn" | "shadow";
  message?: string;
  durationMs?: number;
}

export interface RunToolchainChecksOptions {
  /** Absolute path to toolchain-contracts YAML */
  toolchainsPath: string;
  /** Absolute path to artifact-contracts YAML (optional, for manifest fast path) */
  artifactsPath?: string;
  /** Working directory for subprocess execution */
  cwd?: string;
  /** Filter: only run these check types */
  checkTypes?: string[];
  /** Filter: only run checks for these toolchain IDs */
  toolchainFilter?: string[];
  /** Policy-resolved action per check type. Key = check type, value = action. Special key "_default" for fallback. */
  checkTypeActions?: Record<string, "block" | "warn" | "shadow">;
  /** Default action when no policy match */
  defaultAction?: "block" | "warn" | "shadow";
  /**
   * Optional observer emit function. When provided, each check execution emits a
   * toolchain.execution span (open before the check, close after) so the check is
   * recorded on the agent axis. No-op when undefined (observer is optional).
   * This is OBSERVATION ONLY — emitting spans never alters the returned results
   * nor the resolved action, so workflow success/failure semantics are unchanged.
   */
  observer?: ObserverEmitFn;
  /** Parent span id (typically the enclosing agent.session run_id) for correlation. */
  parentSpanId?: string;
  /** Component id recorded on toolchain.execution spans (agent.component_id). */
  componentId?: string;
  /**
   * Observability correlation ids propagated to spawned check subprocesses as
   * AAAC_SESSION_ID / AAAC_TRACE_ID / AAAC_RUN_ID (#115). A check that spawns
   * `git commit`/`git push` lets the resulting git `post-commit` hook attribute
   * its promotion.* events back to this session. No-op when undefined.
   */
  observabilityIds?: ObservabilityIds;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveAction(
  checkType: string,
  checkTypeActions?: Record<string, "block" | "warn" | "shadow">,
  defaultAction?: "block" | "warn" | "shadow",
): "block" | "warn" | "shadow" {
  if (checkTypeActions?.[checkType]) return checkTypeActions[checkType];
  if (checkTypeActions?.["_default"]) return checkTypeActions["_default"];
  return defaultAction ?? "warn";
}

async function tryManifestFastPath(
  toolchainId: string,
  cwd: string,
): Promise<ToolchainCheckResult | null> {
  const manifestPath = resolve(cwd, MANIFEST_FILENAME);
  if (!existsSync(manifestPath)) return null;

  try {
    const raw = await readFile(manifestPath, "utf-8");
    const parsed = ToolchainManifestSchema.parse(JSON.parse(raw));

    if (parsed.toolchain_id !== toolchainId) return null;

    // Manifest found and toolchain_id matches — Phase 7 will add full
    // hash comparison; for now we treat a matching manifest as a pass.
    return {
      toolchainId,
      checkType: "drift",
      status: "pass",
      action: "warn",
      message: `Manifest fast path: ${MANIFEST_FILENAME} found, toolchain_id matches`,
      durationMs: 0,
    };
  } catch {
    return null;
  }
}

async function executeCheck(
  command: string,
  cwd: string,
  observabilityIds?: ObservabilityIds,
): Promise<{ exitCode: number; stdout: string; stderr: string }> {
  const env = observabilityIds
    ? buildObservabilityEnv(observabilityIds)
    : undefined;
  try {
    const { stdout, stderr } = await execFileAsync("sh", ["-c", command], {
      cwd,
      timeout: SUBPROCESS_TIMEOUT_MS,
      ...(env ? { env } : {}),
    });
    return { exitCode: 0, stdout, stderr };
  } catch (err: unknown) {
    const execErr = err as { code?: number | string; killed?: boolean; stdout?: string; stderr?: string };
    if (execErr.killed) {
      throw new Error(`Command timed out after ${SUBPROCESS_TIMEOUT_MS}ms: ${command}`);
    }
    const exitCode = typeof execErr.code === "number" ? execErr.code : 1;
    return {
      exitCode,
      stdout: execErr.stdout ?? "",
      stderr: execErr.stderr ?? "",
    };
  }
}

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

export async function runToolchainChecks(
  options: RunToolchainChecksOptions,
): Promise<ToolchainCheckResult[]> {
  const {
    toolchainsPath,
    cwd: workingDir,
    checkTypes,
    toolchainFilter,
    checkTypeActions,
    defaultAction,
    observer,
    parentSpanId,
    componentId,
    observabilityIds,
  } = options;

  const cwd = workingDir ?? process.cwd();
  const contracts = await parseToolchainFile(toolchainsPath);
  const results: ToolchainCheckResult[] = [];

  for (const [toolchainId, toolchain] of Object.entries(contracts.toolchains)) {
    if (!toolchain.checks) continue;
    if (toolchainFilter && !toolchainFilter.includes(toolchainId)) continue;

    for (const [checkType, command] of Object.entries(toolchain.checks)) {
      if (checkTypes && !checkTypes.includes(checkType)) continue;

      const action = resolveAction(checkType, checkTypeActions, defaultAction);
      const start = performance.now();

      // ── toolchain.execution open ────────────────────────────────────────
      // Open the span BEFORE running the check so the close span's duration
      // reflects real wall-clock time. No-op when observer is undefined.
      const spanId = emitToolchainOpen(
        observer,
        parentSpanId,
        toolchainId,
        checkType,
        [],
        componentId,
      );

      let result: ToolchainCheckResult;

      // Drift fast path via manifest
      const fastResult =
        checkType === "drift" && options.artifactsPath
          ? await tryManifestFastPath(toolchainId, cwd)
          : null;

      if (fastResult) {
        fastResult.action = action;
        result = fastResult;
      } else {
        try {
          const { exitCode, stderr } = await executeCheck(command, cwd, observabilityIds);
          const durationMs = Math.round(performance.now() - start);
          result = {
            toolchainId,
            checkType,
            status: exitCode === 0 ? "pass" : "fail",
            action,
            message: exitCode === 0 ? undefined : stderr.trim() || `exit code ${exitCode}`,
            durationMs,
          };
        } catch (err) {
          const durationMs = Math.round(performance.now() - start);
          result = {
            toolchainId,
            checkType,
            status: "error",
            action,
            message: (err as Error).message,
            durationMs,
          };
        }
      }

      results.push(result);

      // ── toolchain.execution close ───────────────────────────────────────
      // Status is derived from the check result: pass → success, otherwise
      // failure (carrying the message). The action ("block"/"warn"/"shadow")
      // is recorded as data only and never gates the workflow here.
      emitToolchainClose(
        observer,
        spanId ?? "",
        toolchainId,
        checkType,
        result.durationMs ?? Math.round(performance.now() - start),
        result.status === "pass" ? "success" : "failure",
        result.status === "pass" ? undefined : result.message,
      );
    }
  }

  return results;
}
